/**
 * Contact Query Keys
 *
 * Centralized query key factory for all contact-related queries.
 */

import type { ContactFilters } from './types';

/**
 * Contact query key factory
 *
 * @example
 * ```typescript
 * // Invalidate all contacts for a matter
 * queryClient.invalidateQueries({ queryKey: contactKeys.forMatter(matterId) })
 *
 * // Get specific contact
 * useQuery({ queryKey: contactKeys.detail(matterId, contactId), queryFn: ... })
 * ```
 */
export const contactKeys = {
  /**
   * Root key for all contact queries
   */
  all: ['contacts'] as const,

  /**
   * Base key for contact lists
   */
  lists: () => [...contactKeys.all, 'list'] as const,

  /**
   * Contacts for a specific matter
   * @param matterId - The matter ID
   */
  forMatter: (matterId: number) => [...contactKeys.lists(), matterId] as const,

  /**
   * Filtered contact list
   * @param matterId - The matter ID
   * @param filters - Optional filters
   */
  list: (matterId: number, filters?: ContactFilters) =>
    filters
      ? ([...contactKeys.forMatter(matterId), filters] as const)
      : contactKeys.forMatter(matterId),

  /**
   * What merging one contact into another would do (VALE-782 / VALE-1110).
   * Keyed by direction: swapping which record survives is a different preview.
   * @param survivorId - The contact that would be kept
   * @param removedId - The contact that would be merged into it
   */
  mergePreview: (survivorId: number, removedId: number) =>
    [...contactKeys.all, 'merge-preview', survivorId, removedId] as const,

  /**
   * Base key for contact details
   */
  details: () => [...contactKeys.all, 'detail'] as const,

  /**
   * Specific contact detail
   * @param contactId - The contact ID
   */
  detail: (contactId: number) => [...contactKeys.details(), contactId] as const,

  /**
   * Contact with matter context
   * @param matterId - The matter ID
   * @param contactId - The contact ID
   */
  item: (matterId: number, contactId: number) =>
    [...contactKeys.forMatter(matterId), 'item', contactId] as const,

  /**
   * Contact roles for a matter
   * @param matterId - The matter ID
   */
  roles: (matterId: number) =>
    [...contactKeys.forMatter(matterId), 'roles'] as const,

  /**
   * Forms associated with a contact
   * @param contactId - The contact ID
   */
  forms: (contactId: number) =>
    [...contactKeys.detail(contactId), 'forms'] as const
} as const;
